import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { colors } from '@modernhealth/vital-foundation';
import {
    BodyTextSmall,
    ComponentTextMedium,
    ComponentTextSmall,
    Icon,
    pxToRem,
} from '@modernhealth/vital-components';
import { Menu, MenuList as ML, MenuButton as MB, MenuItem as MI } from '@reach/menu-button';
import styled from '@emotion/styled';

import { navigationService } from 'src/config/rootService';
import { getFullName } from 'src/modules/user/selectors/userSelectors';
import { config } from 'src/modules/admin/config/config';
import userIcon from 'src/public/img/icon-user-blue.svg';
import toolIcon from 'src/public/img/icon-wrench-tool.svg';
import openBookIcon from 'src/public/img/icon-open-book.svg';
import * as S from 'src/shared/layout/styles';

const MenuButton = styled(MB)({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: pxToRem(16),
    backgroundColor: colors.white,
    border: 'none',
    position: 'relative',
    top: 0,
    left: 0,
    '&:hover': {
        backgroundColor: colors.blue05,
        cursor: 'pointer',
        borderRadius: pxToRem(8),
    },
});

const MenuButtonLabel = styled(ComponentTextMedium)({
    margin: pxToRem(0, 8),
});

const MenuList = styled(ML)({
    display: 'flex',
    flexDirection: 'column',
    minWidth: pxToRem(304),
    border: 'none',
    borderRadius: pxToRem(12),
    padding: pxToRem(16),
    backgroundColor: colors.white,
    boxShadow: '0px 0px 8px rgba(33, 38, 46, 0.12), 0px 4px 12px rgba(33, 38, 46, 0.16)',
    position: 'absolute',
});

const MenuItem = styled(MI)({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'start',
    alignItems: 'flex-start',
    gap: pxToRem(4),
    padding: pxToRem(16),
    backgroundColor: colors.white,
    height: '100%',
    '&:hover': {
        backgroundColor: colors.blue05,
        cursor: 'pointer',
        borderRadius: pxToRem(8),
    },
});

const Label = styled(ComponentTextMedium)({
    borderRadius: pxToRem(12),
});

const Description = styled(BodyTextSmall)({
    borderRadius: pxToRem(12),
    padding: pxToRem(4, 0),
});

const AdminBadge = styled(ComponentTextSmall)({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: pxToRem(5),
    width: pxToRem(104),
    maxHeight: pxToRem(21),
    backgroundColor: colors.blue05,
    textAlign: 'center',
    color: colors.blue55,
    margin: pxToRem(0, 16),
    padding: pxToRem(4, 0),
    '&:hover': {
        color: colors.blue55,
    },
});

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
});

interface Props {
    onLogOut: () => void;
}

export const AdminProfileMenu: React.FC<Props> = ({ onLogOut }) => {
    const { t } = useTranslation();
    const fullName = useSelector(getFullName);

    return (
        <Wrapper>
            <AdminBadge>{t('admin:admin_view')}</AdminBadge>
            <Menu>
                <MenuButton>
                    {/* // todo: use <Icon /> once icon type is available in Vita:
                        https://gitlab.com/modernhealth/member-webapp-frontend/-/merge_requests/2468
                     */}
                    <img src={userIcon} color={colors.blue05} alt="user icon" />
                    <S.UserName bold>{fullName}</S.UserName>
                    <Icon name="triangle-down" />
                </MenuButton>
                <MenuList>
                    <MenuItem onSelect={() => navigationService.navigate('/dashboard')}>
                        <Description bold>{t('admin:switch_to_member_view')}</Description>
                    </MenuItem>
                    <MenuItem onSelect={() => onLogOut()}>
                        <Description bold>{t('shared:header.log_out')}</Description>
                    </MenuItem>
                </MenuList>
            </Menu>
        </Wrapper>
    );
};

export const AdminResourcesMenu: React.FC = () => {
    const { t } = useTranslation();
    return (
        <Wrapper>
            <Menu>
                <MenuButton>
                    {/* // todo: use <Icon /> once icon type is available in Vita:
                        https://gitlab.com/modernhealth/member-webapp-frontend/-/merge_requests/2468
                     */}
                    <img src={openBookIcon} color={colors.blue05} alt="user icon" />
                    <MenuButtonLabel bold>{t('admin:menu.resources.button.title')}</MenuButtonLabel>
                    <Icon name="triangle-down" />
                </MenuButton>
                <MenuList>
                    <MenuItem onSelect={() => window.open(config.clientPortalUrl, '_blank')}>
                        <Label bold>
                            {t('admin:menu.resources.menu_item.engagement_resources.label')}
                        </Label>
                        <Description>
                            {t('admin:menu.resources.menu_item.engagement_resources.description')}
                        </Description>
                    </MenuItem>
                </MenuList>
            </Menu>
        </Wrapper>
    );
};

export const AdminToolsMenu: React.FC = () => {
    const { t } = useTranslation();
    return (
        <Wrapper>
            <Menu>
                <MenuButton>
                    {/* // todo: use <Icon /> once icon type is available in Vita:
                        https://gitlab.com/modernhealth/member-webapp-frontend/-/merge_requests/2468
                     */}
                    <img src={toolIcon} color={colors.blue55} alt="user icon" />
                    <MenuButtonLabel bold>{t('admin:menu.tools.button.title')}</MenuButtonLabel>
                    <Icon name="triangle-down" />
                </MenuButton>
                <MenuList>
                    <MenuItem onSelect={() => navigationService.navigate('/admin/benefits')}>
                        <Label bold>{t('admin:menu.tools.menu_item.manage_benefits.label')}</Label>
                        <Description>
                            {t('admin:menu.tools.menu_item.manage_benefits.description')}
                        </Description>
                    </MenuItem>
                    <MenuItem onSelect={() => navigationService.navigate('/admin/manage')}>
                        <Label bold>{t('admin:menu.tools.menu_item.manage_admin.label')}</Label>
                        <Description>
                            {t('admin:menu.tools.menu_item.manage_admin.description')}
                        </Description>
                    </MenuItem>
                    <MenuItem onSelect={() => navigationService.navigate('/admin/hris-sync')}>
                        <Label bold>{t('admin:menu.tools.menu_item.hris_sync.label')}</Label>
                        <Description>
                            {t('admin:menu.tools.menu_item.hris_sync.description')}
                        </Description>
                    </MenuItem>
                </MenuList>
            </Menu>
        </Wrapper>
    );
};
